// Full SVG line chart for a single measurement kind, plotted against age
// in weeks (x). Optionally overlays LMS percentile bands (TNO-style green
// bands) when growth_show_curves is on and the baby's sex is known.

import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import type { Measurement } from '@shared/types.ts';
import type { MeasurementKind } from '@shared/constants.ts';
import { growth } from '@shared/index.ts';
import { KIND_META, ageDaysAt, fmtAge, fmtMeasurement, fmtMeasurementValue } from '../../lib/measure.ts';

const MIN_Y_SPAN: Record<MeasurementKind, number> = {
  weight: 200,
  length: 20,
  head_circumference: 20,
  // 1.0 °C in centideg-tenths so a single reading isn't a flat zero-span chart.
  temperature: 10,
};

// Map MeasurementKind → LMS metric. NL1997 HC tables shipped in T79, so HC
// now joins weight + length. Temperature stays out of the curve flow.
const KIND_TO_METRIC: Partial<Record<MeasurementKind, growth.Metric>> = {
  weight: 'weight',
  length: 'length',
  head_circumference: 'head_circumference',
};

type Props = {
  kind: MeasurementKind;
  birthDate: string;
  measurements: Measurement[];
  /** When true and `sex` is set, overlay LMS percentile bands. */
  showCurves?: boolean;
  reference?: growth.ReferenceId;
  sex?: 'M' | 'F' | null;
  /** Optional override for the right edge of the x-axis (in days from birth).
   *  Defaults to the larger of the latest data point or 60 days. Pass a
   *  larger value in the full-screen view so bands stretch forward. */
  xMaxDays?: number;
  /** Override the rendered chart height (px). Card uses 240. */
  height?: number;
  /** Enable tap/drag inspect. Shows a vertical guide + readout for the age
   *  under the pointer (median P50 from the reference, and a projection at
   *  the baby's current SDS if known). */
  interactive?: boolean;
  /** Baby's latest SDS — used to draw the projection line in the readout
   *  when `interactive` is on. */
  currentSds?: number | null;
};

export function GrowthChart({
  kind,
  birthDate,
  measurements,
  showCurves,
  reference = 'nl5',
  sex,
  xMaxDays,
  height = 240,
  interactive = false,
  currentSds = null,
}: Props) {
  const meta = KIND_META[kind];
  const accent = `var(--${meta.accent})`;
  const sorted = [...measurements].sort((a, b) => a.occurred_at - b.occurred_at);

  const metric = KIND_TO_METRIC[kind] ?? null;
  // LMS tables are in display units (kg for weight, cm for length/head); raw
  // values are integer grams or mm.
  const lmsToRawUnit = (lms: number): number => (kind === 'weight' ? lms * 1000 : lms * 10);

  // Compute the percentile-band samples up front so they participate in the
  // y-axis range calculation (otherwise the bands would clip).
  const bands = useMemo(() => {
    if (!showCurves || !sex || !metric) return null;
    const table = growth.getReferenceTable(reference, metric, sex);
    if (!table) return null;
    const lastDays = sorted.length
      ? ageDaysAt(birthDate, sorted[sorted.length - 1].occurred_at)
      : 30;
    const maxAgeYr = Math.max(lastDays, xMaxDays ?? 0, 60) / 365.25;
    const sampleCount = (xMaxDays ?? 0) > 200 ? 120 : 60;
    const samples = growth.sampleBands(table, 0, maxAgeYr, sampleCount, growth.STANDARD_BAND_SDS);
    if (samples.length === 0) return null;
    return samples;
  }, [showCurves, sex, reference, metric, sorted.length, birthDate, sorted[sorted.length - 1]?.occurred_at, xMaxDays]);

  const pts = sorted.map((m) => ({
    ageDays: ageDaysAt(birthDate, m.occurred_at),
    value: m.value,
    m,
  }));

  // NB: do NOT early-return here — it would skip the useMemo calls below
  // and trip React's hook-count check when switching tabs.
  const isEmpty = sorted.length === 0 && !bands;

  const xMaxFromData = pts.length ? pts[pts.length - 1].ageDays : 7;
  // Extend x to cover the band range (so even a single point shows context),
  // but don't go past where the LMS table actually has values — otherwise the
  // chart would draw axis ticks for ages the curves can't render.
  const xMaxFromBands = bands ? Math.round(bands[bands.length - 1].ageYr * 365.25) : 0;
  const xRequested = Math.max(7, xMaxFromData, xMaxFromBands, xMaxDays ?? 0);
  const xFullMax = bands ? Math.min(xRequested, xMaxFromBands) : xRequested;

  // Zoom — only meaningful in interactive mode. Two-thumb slider below the
  // chart drives [zoomStart, zoomEnd] in days; the chart re-fits y to the
  // visible window so a tight zoom feels useful.
  //
  // Default to a view that frames the existing data plus an 8-week buffer
  // (or 12 weeks total when there's no data yet). The full-range view
  // makes early data look like a sliver in the corner — bands sweep up
  // to 18 months but the line is tiny. User can drag thumbs to extend.
  const [zoom, setZoom] = useState<{ start: number; end: number } | null>(() => {
    if (!interactive) return null;
    const lastDays = pts.length ? pts[pts.length - 1].ageDays : 0;
    const end = Math.max(lastDays + 56, 84);
    if (end >= xFullMax) return null; // nothing to zoom — full range fits
    return { start: 0, end };
  });
  const xMin = interactive && zoom ? zoom.start : 0;
  const xMax = interactive && zoom ? zoom.end : xFullMax;

  // Y range from the points + band samples that fall inside the visible
  // x-window. When zoomed in we want the y-axis to refit, not stay anchored
  // to the full-range extremes.
  const visiblePts = pts.filter((p) => p.ageDays >= xMin && p.ageDays <= xMax);
  let yLo = visiblePts.length
    ? Math.min(...visiblePts.map((p) => p.value))
    : Number.POSITIVE_INFINITY;
  let yHi = visiblePts.length
    ? Math.max(...visiblePts.map((p) => p.value))
    : Number.NEGATIVE_INFINITY;
  if (bands) {
    for (const sample of bands) {
      const ageDays = sample.ageYr * 365.25;
      if (ageDays < xMin || ageDays > xMax) continue;
      for (const v of sample.values) {
        if (v == null) continue;
        const raw = lmsToRawUnit(v);
        if (raw < yLo) yLo = raw;
        if (raw > yHi) yHi = raw;
      }
    }
  }
  if (!Number.isFinite(yLo) || !Number.isFinite(yHi)) {
    yLo = 0;
    yHi = MIN_Y_SPAN[kind];
  }
  const minSpan = MIN_Y_SPAN[kind];
  let y0: number;
  let y1: number;
  if (yHi - yLo < minSpan) {
    const center = (yLo + yHi) / 2;
    y0 = Math.max(0, center - minSpan / 2);
    y1 = center + minSpan / 2;
  } else {
    const yPad = Math.max(1, (yHi - yLo) * 0.05);
    y0 = Math.max(0, yLo - yPad);
    y1 = yHi + yPad;
  }

  // Logical viewBox W is fixed; H is sized to the container's actual
  // pixel aspect so preserveAspectRatio="meet" fills both axes (no
  // letterbox top/bottom or left/right). Falls back to a sensible
  // default for the first render before we have a measurement.
  const W = 340;
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const [wrapW, setWrapW] = useState(0);
  useLayoutEffect(() => {
    if (!wrapRef.current) return;
    const ro = new ResizeObserver(([entry]) => {
      setWrapW(entry.contentRect.width);
    });
    ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, []);
  const H = useMemo(() => {
    if (!interactive) return 220;
    if (wrapW <= 0) return 420;
    // viewBox H should give the same aspect as the rendered <svg> so
    // meet doesn't waste pixels. height is the requested CSS px height
    // of the SVG; W is fixed at 340.
    return Math.round((height / wrapW) * W);
  }, [interactive, wrapW, height]);
  const padL = 40;
  const padR = 12;
  const padT = 12;
  const padB = 26;
  const innerW = W - padL - padR;
  const innerH = H - padT - padB;

  const x = (ageDays: number) => padL + ((ageDays - xMin) / Math.max(1, xMax - xMin)) * innerW;
  const y = (value: number) => padT + (1 - (value - y0) / Math.max(1, y1 - y0)) * innerH;

  const linePath = pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'}${x(p.ageDays).toFixed(1)},${y(p.value).toFixed(1)}`)
    .join(' ');
  const areaPath = pts.length
    ? `${linePath} L${x(pts[pts.length - 1].ageDays).toFixed(1)},${padT + innerH} L${x(pts[0].ageDays).toFixed(1)},${padT + innerH} Z`
    : '';

  // Y gridlines / axis labels — denser in full-screen because the chart is
  // way taller and 3 labels across 600px feels sparse.
  const rawTicks = niceTicks(y0, y1, interactive ? 8 : 4);
  const yTicks: number[] = [];
  const seenLabels = new Set<string>();
  for (const t of rawTicks) {
    const label = fmtMeasurementValue(kind, t);
    if (seenLabels.has(label)) continue;
    seenLabels.add(label);
    yTicks.push(t);
  }
  const weeksTick = pickWeekStride(xMax);

  // Pre-compute band path strings if applicable.
  // bands[t] = { ageYr, values: [Y(-2.5), Y(-2), Y(-1), Y(0), Y(+1), Y(+2), Y(+2.5)] }
  // Between each adjacent pair of SDS lines we build a closed polygon.
  const bandShapes = useMemo(() => {
    if (!bands) return null;
    const sdsCount = growth.STANDARD_BAND_SDS.length;
    // For each band index i in [0..sdsCount-2], make a polygon between line i and i+1.
    const polys: string[] = [];
    for (let i = 0; i < sdsCount - 1; i++) {
      const top: string[] = [];
      const bot: string[] = [];
      for (const s of bands) {
        const vTop = s.values[i + 1];
        const vBot = s.values[i];
        if (vTop == null || vBot == null) continue;
        const ageDays = s.ageYr * 365.25;
        top.push(`${x(ageDays).toFixed(1)},${y(lmsToRawUnit(vTop)).toFixed(1)}`);
        bot.push(`${x(ageDays).toFixed(1)},${y(lmsToRawUnit(vBot)).toFixed(1)}`);
      }
      if (top.length === 0) {
        polys.push('');
        continue;
      }
      polys.push(`M${top.join(' L')} L${bot.reverse().join(' L')} Z`);
    }
    return polys;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bands, xMax, y0, y1]);

  // Median line
  const medianPath = useMemo(() => {
    if (!bands) return '';
    const medianIdx = growth.STANDARD_BAND_SDS.indexOf(0);
    const pts2: string[] = [];
    for (const s of bands) {
      const v = s.values[medianIdx];
      if (v == null) continue;
      const ageDays = s.ageYr * 365.25;
      pts2.push(`${x(ageDays).toFixed(1)},${y(lmsToRawUnit(v)).toFixed(1)}`);
    }
    if (!pts2.length) return '';
    return `M${pts2.join(' L')}`;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bands, xMax, y0, y1]);

  // SDS band fills, lightest on the extremes. Each band index i corresponds
  // to the area between SDS[i] and SDS[i+1].
  // Order: [-2.5,-2,-1,0,+1,+2,+2.5] → 6 bands.
  const bandFills = [
    'color-mix(in oklab, var(--good) 8%, transparent)',  // -2.5..-2
    'color-mix(in oklab, var(--good) 18%, transparent)', // -2..-1
    'color-mix(in oklab, var(--good) 30%, transparent)', // -1..0
    'color-mix(in oklab, var(--good) 30%, transparent)', // 0..+1
    'color-mix(in oklab, var(--good) 18%, transparent)', // +1..+2
    'color-mix(in oklab, var(--good) 8%, transparent)',  // +2..+2.5
  ];

  // Tap/drag inspect — record the pointer x in *viewBox* coordinates and
  // light up the nearest band sample. We get the actual SVG pixel position
  // and back-project through preserveAspectRatio="none" so a finger at
  // any width maps cleanly.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [hoverX, setHoverX] = useState<number | null>(null);

  function onPointer(e: React.PointerEvent<SVGSVGElement>) {
    if (!interactive) return;
    const svg = svgRef.current;
    if (!svg) return;
    // Use the SVG's CTM so we account for preserveAspectRatio letterboxing —
    // a getBoundingClientRect-based ratio would map onto the SVG element box
    // (which can be taller than the drawn chart) and skew the readout.
    const ctm = svg.getScreenCTM();
    if (!ctm) return;
    const inv = ctm.inverse();
    const pt = new DOMPoint(e.clientX, e.clientY).matrixTransform(inv);
    const clamped = Math.max(padL, Math.min(W - padR, pt.x));
    setHoverX(clamped);
  }

  // Inverse of x(): viewBox-x → ageDays.
  const ageDaysFromVbX = (vbX: number) =>
    xMin + ((vbX - padL) / innerW) * Math.max(1, xMax - xMin);

  // Projection line — extends forward from the latest data point. Anchored
  // at (ageDays, value) of that point so the dot sits on the line.
  //
  // Not memoised: x() and y() close over y0/y1/xMin/xMax which can change
  // independently of the deps we'd naturally pick (bands ref, currentSds,
  // pts value). useMemo cached a stale path computed against an older y()
  // closure, so the anchor ended up at the right value but wrong pixel.
  // Recompute every render — it's cheap.
  let projectionPath = '';
  if (interactive && currentSds != null && sex && metric && bands && pts.length) {
    const table = growth.getReferenceTable(reference, metric, sex);
    if (table) {
      const latest = pts[pts.length - 1];
      const out: string[] = [
        `${x(latest.ageDays).toFixed(1)},${y(latest.value).toFixed(1)}`,
      ];
      for (const s of bands) {
        const ageDays = s.ageYr * 365.25;
        if (ageDays <= latest.ageDays) continue;
        const lms = growth.interpolateLMS(table, s.ageYr);
        if (!lms) continue;
        const v = growth.sdsToValue(currentSds, lms);
        if (v == null) continue;
        out.push(`${x(ageDays).toFixed(1)},${y(lmsToRawUnit(v)).toFixed(1)}`);
      }
      if (out.length >= 2) projectionPath = `M${out.join(' L')}`;
    }
  }

  // Readout content for the inspector at the current hover age. Renders the
  // median P50 and (if currentSds is set) the projected value.
  const inspect = useMemo(() => {
    if (!interactive || hoverX == null || !metric || !sex) return null;
    const table = growth.getReferenceTable(reference, metric, sex);
    if (!table) return null;
    const ageDays = ageDaysFromVbX(hoverX);
    const ageYr = ageDays / 365.25;
    const lms = growth.interpolateLMS(table, ageYr);
    if (!lms) return { ageDays, median: null, projected: null };
    const median = lms.M;
    const projected = currentSds == null ? null : growth.sdsToValue(currentSds, lms);
    return { ageDays, median, projected };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive, hoverX, metric, sex, reference, currentSds, xMax]);

  if (isEmpty) {
    return (
      <div
        style={{
          padding: '40px 16px',
          textAlign: 'center',
          color: 'var(--text-3)',
          fontSize: 13,
        }}
      >
        No {meta.label.toLowerCase()} data yet.
      </div>
    );
  }

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{ width: '100%', height, display: 'block', touchAction: interactive ? 'none' : undefined }}
        onPointerDown={onPointer}
        onPointerMove={onPointer}
        onPointerLeave={interactive ? () => setHoverX(null) : undefined}
      >
        <defs>
          <linearGradient id={`grad-${kind}`} x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor={accent} stopOpacity="0.22" />
            <stop offset="100%" stopColor={accent} stopOpacity="0" />
          </linearGradient>
          <clipPath id={`plot-${kind}`}>
            <rect x={padL} y={padT} width={innerW} height={innerH} />
          </clipPath>
        </defs>

        {/* y gridlines */}
        {yTicks.map((t) => (
          <g key={`y-${t}`}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y(t)}
              y2={y(t)}
              stroke="var(--divider)"
              strokeWidth={1}
            />
            <text
              x={padL - 8}
              y={y(t) + 3}
              textAnchor="end"
              fontFamily="Geist Mono, ui-monospace"
              fontSize={9}
              fill="var(--text-3)"
            >
              {fmtMeasurementValue(kind, t)}
            </text>
          </g>
        ))}
        {/* x ticks */}
        {Array.from({ length: Math.floor(xMax / (weeksTick * 7)) + 1 }, (_, i) => i * weeksTick * 7).map(
          (d) => (
            <g key={`x-${d}`}>
              <line
                x1={x(d)}
                x2={x(d)}
                y1={padT + innerH}
                y2={padT + innerH + 4}
                stroke="var(--text-3)"
                strokeWidth={1}
              />
              <text
                x={x(d)}
                y={padT + innerH + 16}
                textAnchor="middle"
                fontFamily="Geist Mono, ui-monospace"
                fontSize={9}
                fill="var(--text-3)"
              >
                {d === 0 ? 'birth' : `${Math.round(d / 7)}w`}
              </text>
            </g>
          ),
        )}
        {/* Plot-area content goes inside the clipPath so a tight zoom or
            stray sample doesn't draw over the y-axis labels / padding. */}
        <g clipPath={`url(#plot-${kind})`}>
        {/* Percentile bands (drawn first so everything else sits on top) */}
        {bandShapes &&
          bandShapes.map((d, i) => (d ? <path key={`band-${i}`} d={d} fill={bandFills[i]} /> : null))}
        {/* Median */}
        {medianPath && (
          <path
            d={medianPath}
            fill="none"
            stroke="color-mix(in oklab, var(--good) 60%, var(--text-2))"
            strokeWidth={1}
            strokeDasharray="3 3"
            opacity={0.9}
          />
        )}
        {/* Data area + line + points (drawn last). The accent area is muted
            when curves are on so the bands stay readable. */}
        {pts.length > 0 && !bands && <path d={areaPath} fill={`url(#grad-${kind})`} />}
        {pts.length > 0 && (
          <path
            d={linePath}
            fill="none"
            stroke={accent}
            strokeWidth={1.6}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        )}
        {/* Projection — extends the baby's current SDS forward through the
            band range. Only drawn in interactive (full-screen) mode. */}
        {projectionPath && (
          <path
            d={projectionPath}
            fill="none"
            stroke={accent}
            strokeWidth={1}
            strokeDasharray="2 3"
            opacity={0.55}
          />
        )}
        {pts.map((p, i) => (
          <circle
            key={p.m.id}
            cx={x(p.ageDays)}
            cy={y(p.value)}
            r={i === pts.length - 1 ? 4 : 2.4}
            fill={accent}
            stroke="var(--bg-card)"
            strokeWidth={1.2}
          />
        ))}
        {/* Hover guide + intersection dot on the projection line. The dot
            makes the inspector readout feel anchored to the curve rather
            than floating below the chart. */}
        {interactive && hoverX != null && (
          <line
            x1={hoverX}
            x2={hoverX}
            y1={padT}
            y2={padT + innerH}
            stroke="var(--text-2)"
            strokeWidth={0.8}
            strokeDasharray="2 2"
            opacity={0.6}
          />
        )}
        {interactive && hoverX != null && inspect?.projected != null && (
          <circle
            cx={hoverX}
            cy={y(lmsToRawUnit(inspect.projected))}
            r={3.5}
            fill={accent}
            stroke="var(--bg-app)"
            strokeWidth={1.2}
          />
        )}
        </g>
      </svg>
      {interactive && (
        <ZoomSlider
          fullMax={xFullMax}
          zoom={zoom}
          onChange={setZoom}
          birthDate={birthDate}
        />
      )}
      {interactive ? (
        // Fixed three-row layout so the page never jumps as the hover moves
        // (text-wrap was different at different ages, and the projection
        // row only renders when currentSds is known).
        <div
          style={{
            marginTop: 10,
            padding: '12px 14px',
            borderRadius: 14,
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            fontSize: 13,
            color: 'var(--text-2)',
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            rowGap: 6,
            columnGap: 14,
            alignItems: 'baseline',
          }}
        >
          <InspectorRow label="age">
            {inspect ? (
              <>
                <span className="lt-tabular" style={{ color: 'var(--text-1)', fontWeight: 600 }}>
                  {fmtAge(Math.round(inspect.ageDays))}
                </span>{' '}
                <span style={{ color: 'var(--text-3)' }}>
                  ({fmtMonYy(birthDate, inspect.ageDays)})
                </span>
              </>
            ) : (
              <span style={{ color: 'var(--text-3)', fontStyle: 'italic' }}>tap the chart</span>
            )}
          </InspectorRow>
          <InspectorRow label="median (P50)">
            {inspect?.median != null ? (
              <span className="lt-tabular" style={{ color: 'var(--text-1)', fontWeight: 600 }}>
                {fmtMeasurement(kind, lmsToRawUnit(inspect.median))}
              </span>
            ) : (
              <span style={{ color: 'var(--text-3)' }}>—</span>
            )}
          </InspectorRow>
          <InspectorRow label="your projection">
            {inspect?.projected != null ? (
              <span className="lt-tabular" style={{ color: accent, fontWeight: 600 }}>
                {fmtMeasurement(kind, lmsToRawUnit(inspect.projected))}
              </span>
            ) : (
              <span style={{ color: 'var(--text-3)' }}>—</span>
            )}
          </InspectorRow>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: 'var(--text-3)', textAlign: 'right', marginTop: 4 }}>
          {bands && sex && (
            <span style={{ marginRight: 8 }}>
              {reference.toUpperCase()} · {sex === 'M' ? 'boys' : 'girls'}
            </span>
          )}
          {pts.length > 0 && (
            <>
              latest:{' '}
              <span className="lt-tabular" style={{ color: 'var(--text-1)', fontWeight: 600 }}>
                {fmtMeasurement(kind, pts[pts.length - 1].value)}
              </span>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// Dual-thumb age-range slider. Drag either thumb to zoom into a window
// of the chart; tap the central track to recentre; tap the reset chip to
// clear back to full range. Days, in the chart's own age units.
function ZoomSlider({
  fullMax,
  zoom,
  onChange,
  birthDate,
}: {
  fullMax: number;
  zoom: { start: number; end: number } | null;
  onChange: (z: { start: number; end: number } | null) => void;
  birthDate: string;
}) {
  const trackRef = useRef<HTMLDivElement | null>(null);
  const MIN_SPAN = 14; // days — keep a usable window so curves stay readable
  const start = zoom?.start ?? 0;
  const end = zoom?.end ?? fullMax;
  const startPct = (start / fullMax) * 100;
  const endPct = (end / fullMax) * 100;

  function pxToDays(clientX: number): number {
    const r = trackRef.current?.getBoundingClientRect();
    if (!r) return 0;
    const ratio = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return Math.round(ratio * fullMax);
  }

  function drag(which: 'start' | 'end') {
    return (e: React.PointerEvent) => {
      e.preventDefault();
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
      const move = (ev: PointerEvent) => {
        const d = pxToDays(ev.clientX);
        if (which === 'start') {
          const ns = Math.max(0, Math.min(end - MIN_SPAN, d));
          onChange({ start: ns, end });
        } else {
          const ne = Math.min(fullMax, Math.max(start + MIN_SPAN, d));
          onChange({ start, end: ne });
        }
      };
      const up = () => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', up);
      };
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', up);
    };
  }

  const zoomed = zoom != null && (zoom.start > 0 || zoom.end < fullMax);

  return (
    <div style={{ marginTop: 2 }}>
      <div
        ref={trackRef}
        style={{
          position: 'relative',
          height: 26,
          margin: '0 8px',
          touchAction: 'none',
        }}
      >
        {/* Track */}
        <div
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            height: 4,
            borderRadius: 2,
            background: 'var(--bg-sunken)',
          }}
        />
        {/* Selected range */}
        <div
          style={{
            position: 'absolute',
            left: `${startPct}%`,
            right: `${100 - endPct}%`,
            top: '50%',
            transform: 'translateY(-50%)',
            height: 4,
            borderRadius: 2,
            background: 'color-mix(in oklab, var(--accent) 60%, transparent)',
          }}
        />
        <Thumb left={`${startPct}%`} onPointerDown={drag('start')} label="Zoom start" />
        <Thumb left={`${endPct}%`} onPointerDown={drag('end')} label="Zoom end" />
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '2px 8px 0',
          fontSize: 10,
          color: 'var(--text-3)',
          minHeight: 18,
        }}
      >
        <span className="lt-tabular">
          {start === 0 ? 'birth' : `${Math.round(start / 7)}w`} · {fmtMonYy(birthDate, start)}
        </span>
        {zoomed ? (
          <button
            type="button"
            onClick={() => onChange(null)}
            style={{
              background: 'transparent',
              color: 'var(--text-2)',
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 999,
              border: '1px solid var(--border)',
            }}
          >
            Reset
          </button>
        ) : (
          <span />
        )}
        <span className="lt-tabular">
          {`${Math.round(end / 7)}w`} · {fmtMonYy(birthDate, end)}
        </span>
      </div>
    </div>
  );
}

function Thumb({
  left,
  onPointerDown,
  label,
}: {
  left: string;
  onPointerDown: (e: React.PointerEvent) => void;
  label: string;
}) {
  return (
    <div
      role="slider"
      aria-label={label}
      onPointerDown={onPointerDown}
      style={{
        position: 'absolute',
        left,
        top: '50%',
        transform: 'translate(-50%, -50%)',
        width: 22,
        height: 22,
        borderRadius: 999,
        background: 'var(--bg-card)',
        border: '2px solid var(--accent)',
        boxShadow: 'var(--shadow-sm)',
        cursor: 'grab',
        touchAction: 'none',
      }}
    />
  );
}

function InspectorRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <>
      <span
        style={{
          color: 'var(--text-3)',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.04,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ color: 'var(--text-2)' }}>{children}</span>
    </>
  );
}

function niceTicks(min: number, max: number, count: number): number[] {
  if (max <= min) return [min];
  const step = (max - min) / count;
  const mag = 10 ** Math.floor(Math.log10(step));
  const norm = step / mag;
  const nice = norm < 1.5 ? 1 : norm < 3 ? 2 : norm < 7 ? 5 : 10;
  const niceStep = nice * mag;
  const start = Math.ceil(min / niceStep) * niceStep;
  const ticks: number[] = [];
  for (let v = start; v <= max + niceStep * 0.001; v += niceStep) ticks.push(v);
  return ticks;
}

// Calendar Mon 'YY label for `birthDate + ageDays`. Birth is shown as
// the actual month so parents can anchor "birth = Apr '26".
function fmtMonYy(birthDate: string, ageDays: number): string {
  const [y, m, d] = birthDate.split('-').map(Number);
  const ms = Date.UTC(y, (m ?? 1) - 1, d ?? 1) + ageDays * 86_400_000;
  const dt = new Date(ms);
  const mon = dt.toLocaleString('en-GB', { month: 'short', timeZone: 'UTC' });
  const yy = String(dt.getUTCFullYear() % 100).padStart(2, '0');
  return `${mon} ’${yy}`;
}

function pickWeekStride(totalDays: number): number {
  const totalWeeks = Math.max(1, totalDays / 7);
  if (totalWeeks <= 8) return 1;
  if (totalWeeks <= 16) return 2;
  if (totalWeeks <= 40) return 4;
  if (totalWeeks <= 80) return 8;
  return 13;
}
